import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  setDoc,
  Timestamp,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { WarehouseGoodsItem } from '../types/warehouseGoodsCatalog';
import {
  inferDefaultGramsPerBaseUnit,
  normalizeGoodsNameKey,
} from '../utils/warehouseGoodsCatalogImportUtils';
import { normalizeCukcukValue } from '../utils/cukcukMapping';

const COLLECTION = 'warehouse-goods-catalog';

const toDate = (v: any): Date => {
  if (!v) return new Date();
  if (v instanceof Date) return v;
  if (v?.toDate) return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? new Date() : d;
};

const normalizeGrams = (raw: any, baseUnit: string): number => {
  const n = Number(raw?.gramsPerBaseUnit);
  if (Number.isFinite(n) && n > 0) return n;
  return inferDefaultGramsPerBaseUnit(baseUnit);
};

const normalizeDoc = (id: string, raw: any): WarehouseGoodsItem => {
  const baseUnit = String(raw.baseUnit || 'g').trim();
  return {
    id,
    productCode: String(raw.productCode || '').trim(),
    name: String(raw.name || '').trim(),
    group: String(raw.group || '').trim(),
    baseUnit,
    gramsPerBaseUnit: normalizeGrams(raw, baseUnit),
    cukcukMaterialCode: normalizeCukcukValue(raw.cukcukMaterialCode),
    cukcukUnit: normalizeCukcukValue(raw.cukcukUnit),
    supplierId: normalizeCukcukValue(raw.supplierId),
    supplierName: normalizeCukcukValue(raw.supplierName),
    sortIndex: typeof raw.sortIndex === 'number' ? raw.sortIndex : Number(raw.sortIndex) || 0,
    createdAt: toDate(raw.createdAt),
    updatedAt: toDate(raw.updatedAt),
  };
};

export const warehouseGoodsCatalogService = {
  async findNameConflict(
    name: string,
    excludeId?: string,
  ): Promise<WarehouseGoodsItem | undefined> {
    const all = await this.getAll();
    const nk = normalizeGoodsNameKey(name);
    if (!nk) return undefined;
    return all.find((e) => normalizeGoodsNameKey(e.name) === nk && e.id !== excludeId);
  },

  async getAll(): Promise<WarehouseGoodsItem[]> {
    const snap = await getDocs(collection(db, COLLECTION));
    return snap.docs
      .map((d) => normalizeDoc(d.id, d.data()))
      .sort((a, b) => {
        const si = (a.sortIndex ?? 0) - (b.sortIndex ?? 0);
        if (si !== 0) return si;
        return (a.productCode || '').localeCompare(b.productCode || '', 'vi');
      });
  },

  async upsertItem(item: Omit<WarehouseGoodsItem, 'createdAt' | 'updatedAt'> & { id?: string }): Promise<void> {
    const sanitized =
      (item.productCode || '').trim().replace(/[^\w\u00C0-\u024f-]+/gi, '_').replace(/^_+|_+$/g, '') || '';
    const ref = item.id
      ? doc(db, COLLECTION, item.id)
      : sanitized
        ? doc(db, COLLECTION, sanitized)
        : doc(collection(db, COLLECTION));
    const now = Timestamp.fromDate(new Date());
    const bu = (item.baseUnit || 'g').trim();
    const grams =
      Number(item.gramsPerBaseUnit) > 0
        ? Number(item.gramsPerBaseUnit)
        : inferDefaultGramsPerBaseUnit(bu);
    const payload: Record<string, unknown> = {
      productCode: item.productCode.trim(),
      name: item.name.trim(),
      group: item.group.trim(),
      baseUnit: bu,
      gramsPerBaseUnit: grams,
      sortIndex: item.sortIndex ?? 0,
      updatedAt: now,
    };
    if (!item.id) {
      payload.createdAt = now;
    }
    if (item.cukcukMaterialCode !== undefined) {
      payload.cukcukMaterialCode = normalizeCukcukValue(item.cukcukMaterialCode);
    }
    if (item.cukcukUnit !== undefined) {
      payload.cukcukUnit = normalizeCukcukValue(item.cukcukUnit);
    }
    if (item.supplierId !== undefined) {
      payload.supplierId = normalizeCukcukValue(item.supplierId);
    }
    if (item.supplierName !== undefined) {
      payload.supplierName = normalizeCukcukValue(item.supplierName);
    }
    await setDoc(ref, payload, { merge: true });
  },

  async updateCukcukMapping(
    id: string,
    mapping: { cukcukMaterialCode?: unknown; cukcukUnit?: unknown },
  ): Promise<void> {
    const materialId = id.trim();
    if (!materialId) throw new Error('Material id is required');

    const payload: Record<string, any> = {
      updatedAt: Timestamp.fromDate(new Date()),
    };
    if (mapping.cukcukMaterialCode !== undefined) {
      payload.cukcukMaterialCode = normalizeCukcukValue(mapping.cukcukMaterialCode);
    }
    if (mapping.cukcukUnit !== undefined) {
      payload.cukcukUnit = normalizeCukcukValue(mapping.cukcukUnit);
    }
    await updateDoc(doc(db, COLLECTION, materialId), payload);
  },

  /** Gộp theo mã sản phẩm: trùng mã thì cập nhật, lạ thì thêm. */
  async bulkUpsertByProductCode(
    items: Omit<WarehouseGoodsItem, 'id' | 'createdAt' | 'updatedAt'>[],
  ): Promise<{ created: number; updated: number }> {
    const existing = await this.getAll();
    const byCode = new Map<string, WarehouseGoodsItem>();
    existing.forEach((e) => {
      const k = (e.productCode || '').trim().toLowerCase();
      if (k) byCode.set(k, e);
    });

    const knownIds = new Set(existing.map((e) => e.id).filter(Boolean) as string[]);
    let created = 0;
    let updated = 0;
    const batchSize = 400;
    let batch = writeBatch(db);
    let n = 0;

    const commitBatch = async () => {
      if (n === 0) return;
      await batch.commit();
      batch = writeBatch(db);
      n = 0;
    };

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const code = (it.productCode || '').trim();
      const key = code.toLowerCase();
      const prev = key ? byCode.get(key) : undefined;
      const id =
        prev?.id ||
        code.replace(/[^\w\u00C0-\u024f-]+/gi, '_').replace(/^_+|_+$/g, '') ||
        `row_${i}_${Date.now()}`;

      const ref = doc(db, COLLECTION, id);
      const now = Timestamp.fromDate(new Date());
      const existed = knownIds.has(id);
      if (existed) updated += 1;
      else {
        knownIds.add(id);
        created += 1;
      }

      const bu = (it.baseUnit || 'g').trim();
      const grams =
        Number(it.gramsPerBaseUnit) > 0
          ? Number(it.gramsPerBaseUnit)
          : inferDefaultGramsPerBaseUnit(bu);

      batch.set(
        ref,
        {
          productCode: code,
          name: it.name.trim(),
          group: it.group.trim(),
          baseUnit: bu,
          gramsPerBaseUnit: grams,
          sortIndex: it.sortIndex ?? i,
          updatedAt: now,
          ...(!existed ? { createdAt: now } : {}),
          ...(it.cukcukMaterialCode !== undefined
            ? { cukcukMaterialCode: normalizeCukcukValue(it.cukcukMaterialCode) }
            : {}),
          ...(it.cukcukUnit !== undefined ? { cukcukUnit: normalizeCukcukValue(it.cukcukUnit) } : {}),
          ...(it.supplierId !== undefined ? { supplierId: normalizeCukcukValue(it.supplierId) } : {}),
          ...(it.supplierName !== undefined ? { supplierName: normalizeCukcukValue(it.supplierName) } : {}),
        },
        { merge: true },
      );
      n += 1;

      byCode.set(key || id, {
        ...it,
        id,
        productCode: code,
        baseUnit: bu,
        gramsPerBaseUnit: grams,
      });

      if (n >= batchSize) {
        await commitBatch();
      }
    }
    await commitBatch();
    return { created, updated };
  },

  async deleteItem(id: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  },

  async deleteAll(): Promise<void> {
    const snap = await getDocs(collection(db, COLLECTION));
    let batch = writeBatch(db);
    let n = 0;
    for (const d of snap.docs) {
      batch.delete(d.ref);
      n += 1;
      if (n >= 400) {
        await batch.commit();
        batch = writeBatch(db);
        n = 0;
      }
    }
    if (n > 0) await batch.commit();
  },
};
